import { type ListingApplicationPayload } from '#/api/ats';
import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type {
  ApiInternshipId,
  ApiJobId,
  ApiListingApplicationId,
  ApiOrgId,
  ApiVolopId,
} from '#/api/types/ApiTypedId';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { useEnvironment, useUser } from '#/contexts/UserEnvironmentContext';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';

import { useListingApplicationSubmissionAsGuest } from './useListingApplicationSubmissionAsGuest';
import { useListingApplicationSubmissionAsUser } from './useListingApplicationSubmissionAsUser';

type Args = {
  listingId: ApiJobId | ApiInternshipId | ApiVolopId;
  listingType: ApiListingApplicationListingType;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  applied: boolean;
  onApplied: (listingApplicationId: ApiListingApplicationId) => void;
  listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
};

export function useListingApplicationSubmission({
  listingId,
  listingType,
  orgId,
  orgType,
  applied,
  onApplied,
  listingApplicationFailure,
}: Args) {
  const { user } = useUser();
  const { environment } = useEnvironment();

  const { getInitialUserValues, submitListingApplicationAsUser } =
    useListingApplicationSubmissionAsUser({
      listingId,
      listingType,
      orgId,
      orgType,
      onApplied,
      listingApplicationFailure,
    });

  const { submitListingApplicationAsGuest } =
    useListingApplicationSubmissionAsGuest({
      listingId,
      listingType,
      applied,
      onApplied,
    });

  const submitListingApplication: FormSubmitFunction<
    ListingApplicationPayload
  > = async ({ values, handleError, updateSubmitting, clearErrors }) => {
    try {
      if (user && environment) {
        await submitListingApplicationAsUser({ user, environment, values });
      } else {
        await submitListingApplicationAsGuest({ values });
        clearErrors();
      }
    } catch (error) {
      handleError(error as Error);
    } finally {
      updateSubmitting(false);
    }
  };

  return {
    submitListingApplication,
    listingApplicationInitialValues:
      user && environment
        ? listingApplicationFailure?.initialValues ||
          getInitialUserValues({ user, environment })
        : {},
    listingApplicationInitialErrors: listingApplicationFailure?.errors ?? {},
  };
}
